"use client";

import type React from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Grid,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  useToast,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabaseClient";

type MaybeNumber = number | null | undefined;

interface DashboardStats {
  totalCalls: MaybeNumber;
  callsAnswered: MaybeNumber;
  contactRate: MaybeNumber;
  totalLeads: MaybeNumber;
  activeAgents: MaybeNumber;
  minutesUsed: MaybeNumber;
  totalSMS: MaybeNumber;
  totalEmails: MaybeNumber;
  totalWhatsApp: MaybeNumber;
  contactRateChange: MaybeNumber;
  totalCallsChange: MaybeNumber;
  smsChangePercent: MaybeNumber;
  whatsappChangePercent: MaybeNumber;
  emailChangePercent: MaybeNumber;
}

interface RecentCall {
  call_id: string;
  agent_name: string;
  start_timestamp: number;
  duration_ms: number;
  call_analysis?: {
    call_successful?: boolean;
    user_sentiment?: string;
  };
  from_number?: string;
  to_number?: string;
}

interface AgentPerformance {
  id: string;
  name: string;
  type: string;
  status: string;
  interactions: number;
  success: number;
}

interface LoadingStates {
  callStats: boolean;
  messageStats: boolean;
  voiceAgents: boolean;
  smsAgents: boolean;
  emailAgents: boolean;
  whatsappAgents: boolean;
  agentPerformance: boolean;
}

const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

interface CachedDashboardData {
  stats: DashboardStats;
  recentCalls: RecentCall[];
  agents: AgentPerformance[];
  timestamp: number;
  companyName: string;
}

// Helper function to get company-specific cache key
const getCacheKey = (companyName: string): string => {
  return `dashboard_stats_cache_${companyName.replace(/\s+/g, "_")}`;
};

// Helper function to get cached data
const getCachedData = (companyName: string): CachedDashboardData | null => {
  try {
    const cacheKey = getCacheKey(companyName);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const data: CachedDashboardData = JSON.parse(cached);

    // Optional: respect cache duration (kept lenient; if you want strict, uncomment)
    // if (Date.now() - data.timestamp > CACHE_DURATION_MS) return null;

    return data;
  } catch (error) {
    console.error("[v0] Error reading cache:", error);
    return null;
  }
};

// Helper function to save data to cache
const saveCacheData = (
  companyName: string,
  stats: DashboardStats,
  recentCalls: RecentCall[],
  agents: AgentPerformance[],
) => {
  try {
    const cacheKey = getCacheKey(companyName);
    const data: CachedDashboardData = {
      stats,
      recentCalls,
      agents,
      timestamp: Date.now(),
      companyName,
    };
    localStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    console.error("[v0] Error saving cache:", error);
  }
};

const isFiniteNumber = (v: any): v is number =>
  typeof v === "number" && Number.isFinite(v);

const SmallInlineSpinner = ({ color = "gray.400" }: { color?: string }) => (
  <Spinner
    size="sm"
    thickness="2px"
    speed="0.6s"
    color={color}
    emptyColor="gray.200"
  />
);

/**
 * Single-spinner top metric:
 * - If "value" missing OR loading => show ONE spinner in StatNumber
 * - StatHelpText:
 *    - hidden while loading/missing (so you never get a 2nd spinner)
 *    - shown only when change value is available
 */
const TopMetricStat = ({
  label,
  subLabel,
  value,
  valueLoading,
  valueColor,
  valueFormatter,
  changeValue,
  changeSuffix = "% from last month",
}: {
  label: string;
  subLabel?: string;
  value: MaybeNumber;
  valueLoading: boolean;
  valueColor: string;
  valueFormatter: (n: number) => string;
  changeValue?: MaybeNumber;
  changeSuffix?: string;
}) => {
  const showSpinner = valueLoading || value == null;
  const showChange = !showSpinner && changeValue != null;

  return (
    <Stat>
      <StatLabel>
        {label}{" "}
        {subLabel ? (
          <Box as="span" fontSize="sm" color="gray.500" fontWeight="normal">
            ({subLabel})
          </Box>
        ) : null}
      </StatLabel>

      <StatNumber color={valueColor}>
        {showSpinner ? (
          <SmallInlineSpinner color={valueColor} />
        ) : (
          valueFormatter(value as number)
        )}
      </StatNumber>

      {showChange ? (
        <StatHelpText>
          <StatArrow
            type={(changeValue as number) >= 0 ? "increase" : "decrease"}
          />
          {Math.abs(changeValue as number).toFixed(1)}
          {changeSuffix}
        </StatHelpText>
      ) : (
        // keep layout consistent without showing a second spinner
        <Box h="20px" />
      )}
    </Stat>
  );
};

const BigValueOrSpinner = ({
  value,
  loading,
  color,
  formatter,
}: {
  value: MaybeNumber;
  loading: boolean;
  color: string;
  formatter: (n: number) => string;
}) => {
  if (loading || value == null) {
    return (
      <Text fontSize="2xl" fontWeight="bold" color={color} minH="32px">
        <SmallInlineSpinner color={color} />
      </Text>
    );
  }
  return (
    <Text fontSize="2xl" fontWeight="bold" color={color} minH="32px">
      {formatter(value)}
    </Text>
  );
};

const emptyStats: DashboardStats = {
  totalCalls: null,
  callsAnswered: null,
  contactRate: null,
  contactRateChange: null,
  totalCallsChange: null,
  minutesUsed: null,
  totalLeads: null,
  totalSMS: null,
  totalEmails: null,
  totalWhatsApp: null,
  activeAgents: null,
  smsChangePercent: null,
  whatsappChangePercent: null,
  emailChangePercent: null,
};

const Dashboard: React.FC = () => {
  const { user, isAdmin, currentClientId, clients, inviteUser, userProfile } =
    useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteCompany, setInviteCompany] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const toast = useToast();

  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    callStats: true,
    messageStats: true,
    voiceAgents: true,
    smsAgents: true,
    emailAgents: true,
    whatsappAgents: true,
    agentPerformance: true,
  });

  const [dashboardStats, setDashboardStats] =
    useState<DashboardStats>(emptyStats);

  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [agents, setAgents] = useState<AgentPerformance[]>([]);

  const isFetchingRef = useRef(false);
  const lastFetchKeyRef = useRef<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);

  const activeAgentsCounts = useRef<{
    voice: MaybeNumber;
    sms: MaybeNumber;
    email: MaybeNumber;
    whatsapp: MaybeNumber;
  }>({
    voice: null,
    sms: null,
    email: null,
    whatsapp: null,
  });

  const companyName = useMemo(() => {
    if (isAdmin && currentClientId) {
      return clients.find((c) => c.id === currentClientId)?.company_name;
    }
    return userProfile?.company_name;
  }, [isAdmin, currentClientId, userProfile?.company_name, clients]);

  const currentClient = clients.find((c) => c.id === currentClientId);
  const displayName =
    isAdmin && currentClient
      ? currentClient.name
      : user?.email?.split("@")[0] || "User";

  const serverUrl = process.env.REACT_APP_SERVER_URL || "http://localhost:8000";

  useEffect(() => {
    // Reset when switching company
    activeAgentsCounts.current = {
      voice: null,
      sms: null,
      email: null,
      whatsapp: null,
    };
    setDashboardStats(emptyStats);

    // reset loading states (so spinners show in-place)
    setLoadingStates({
      callStats: true,
      messageStats: true,
      voiceAgents: true,
      smsAgents: true,
      emailAgents: true,
      whatsappAgents: true,
      agentPerformance: true,
    });
  }, [companyName]);

  useEffect(() => {
    if (companyName) {
      const cachedData = getCachedData(companyName);
      if (cachedData) {
        console.log("[v0] Loading cached dashboard data");
        setDashboardStats({ ...emptyStats, ...cachedData.stats });
        setRecentCalls(cachedData.recentCalls || []);
        setAgents(cachedData.agents || []);

        // cached loaded, turn off loading flags
        setLoadingStates({
          callStats: false,
          messageStats: false,
          voiceAgents: false,
          smsAgents: false,
          emailAgents: false,
          whatsappAgents: false,
          agentPerformance: false,
        });
      }
    }
  }, [companyName]);

  useEffect(() => {
    const fetchKey = `${currentClientId || ""}-${companyName || ""}`;

    if (
      companyName &&
      fetchKey !== lastFetchKeyRef.current &&
      !isFetchingRef.current
    ) {
      lastFetchKeyRef.current = fetchKey;
      fetchDashboardData(companyName);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [companyName, currentClientId]);

  const fetchDashboardData = async (companyName: string) => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    try {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (!authUser) throw new Error("User not authenticated");

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "user-id": authUser.id,
      };

      if (isAdmin && currentClientId) {
        const selectedClient = clients.find((c) => c.id === currentClientId);
        if (selectedClient) {
          headers["x-is-admin"] = "true";
          headers["x-company-name"] = selectedClient.company_name;
        }
      }

      const freshStats: DashboardStats = { ...emptyStats };
      let freshRecentCalls: RecentCall[] = [];
      let freshAgents: AgentPerformance[] = [];

      // show loading now
      setLoadingStates({
        callStats: true,
        messageStats: true,
        voiceAgents: true,
        smsAgents: true,
        emailAgents: true,
        whatsappAgents: true,
        agentPerformance: true,
      });

      await Promise.all([
        fetch(`${serverUrl}/api/dashboard/call-stats`, {
          method: "POST",
          headers,
          body: JSON.stringify({}),
          signal,
        })
          .then(async (res) => {
            if (!res.ok) throw new Error("call-stats failed");
            return res.json();
          })
          .then((data) => {
            if (isFiniteNumber(data?.totalCalls))
              freshStats.totalCalls = data.totalCalls;
            if (isFiniteNumber(data?.callsAnswered))
              freshStats.callsAnswered = data.callsAnswered;
            if (isFiniteNumber(data?.contactRate))
              freshStats.contactRate = data.contactRate;
            if (isFiniteNumber(data?.contactRateChange))
              freshStats.contactRateChange = data.contactRateChange;
            if (isFiniteNumber(data?.totalCallsChange))
              freshStats.totalCallsChange = data.totalCallsChange;
            if (isFiniteNumber(data?.minutesUsed))
              freshStats.minutesUsed = data.minutesUsed;

            if (Array.isArray(data?.recentCalls))
              freshRecentCalls = data.recentCalls;

            setLoadingStates((prev) => ({ ...prev, callStats: false }));
          })
          .catch((error) => {
            if ((error as Error).name !== "AbortError") {
              console.error("Error fetching call stats:", error);
            }
            setLoadingStates((prev) => ({ ...prev, callStats: false }));
          }),

        fetch(`${serverUrl}/api/dashboard/message-stats`, {
          headers,
          signal,
        })
          .then(async (res) => {
            if (!res.ok) throw new Error("message-stats failed");
            return res.json();
          })
          .then((data) => {
            if (isFiniteNumber(data?.totalSMS))
              freshStats.totalSMS = data.totalSMS;
            if (isFiniteNumber(data?.totalEmails))
              freshStats.totalEmails = data.totalEmails;
            if (isFiniteNumber(data?.totalWhatsApp))
              freshStats.totalWhatsApp = data.totalWhatsApp;
            if (isFiniteNumber(data?.totalLeads))
              freshStats.totalLeads = data.totalLeads;

            if (isFiniteNumber(data?.smsChangePercent))
              freshStats.smsChangePercent = data.smsChangePercent;
            if (isFiniteNumber(data?.whatsappChangePercent))
              freshStats.whatsappChangePercent = data.whatsappChangePercent;
            if (isFiniteNumber(data?.emailChangePercent))
              freshStats.emailChangePercent = data.emailChangePercent;

            setLoadingStates((prev) => ({ ...prev, messageStats: false }));
          })
          .catch((error) => {
            if ((error as Error).name !== "AbortError") {
              console.error("Error fetching message stats:", error);
            }
            setLoadingStates((prev) => ({ ...prev, messageStats: false }));
          }),

        fetch(`${serverUrl}/api/dashboard/voice-agents`, {
          headers,
          signal,
        })
          .then(async (res) => {
            if (!res.ok) throw new Error("voice-agents failed");
            return res.json();
          })
          .then((data) => {
            const count = isFiniteNumber(data?.activeCount)
              ? data.activeCount
              : null;
            updateActiveAgentsCount("voice", count);

            const activeAgents = Array.isArray(data?.activeAgents)
              ? data.activeAgents
              : [];
            return updateAgentPerformance(
              activeAgents,
              "Voice",
              authUser.id,
              companyName,
              signal,
            ).then((agentPerf) => {
              freshAgents = agentPerf;
            });
          })
          .catch((error) => {
            if ((error as Error).name !== "AbortError") {
              console.error("Error fetching voice agents:", error);
            }
            setLoadingStates((prev) => ({
              ...prev,
              voiceAgents: false,
              agentPerformance: false,
            }));
          }),

        fetch(`${serverUrl}/api/dashboard/sms-agents`, {
          headers,
          signal,
        })
          .then(async (res) => {
            if (!res.ok) throw new Error("sms-agents failed");
            return res.json();
          })
          .then((data) => {
            const count = isFiniteNumber(data?.activeCount)
              ? data.activeCount
              : null;
            updateActiveAgentsCount("sms", count);
            setLoadingStates((prev) => ({ ...prev, smsAgents: false }));
          })
          .catch((error) => {
            if ((error as Error).name !== "AbortError") {
              console.error("Error fetching SMS agents:", error);
            }
            setLoadingStates((prev) => ({ ...prev, smsAgents: false }));
          }),

        fetch(`${serverUrl}/api/dashboard/email-agents`, {
          headers,
          signal,
        })
          .then(async (res) => {
            if (!res.ok) throw new Error("email-agents failed");
            return res.json();
          })
          .then((data) => {
            const count = isFiniteNumber(data?.activeCount)
              ? data.activeCount
              : null;
            updateActiveAgentsCount("email", count);
            setLoadingStates((prev) => ({ ...prev, emailAgents: false }));
          })
          .catch((error) => {
            if ((error as Error).name !== "AbortError") {
              console.error("Error fetching email agents:", error);
            }
            setLoadingStates((prev) => ({ ...prev, emailAgents: false }));
          }),

        fetch(`${serverUrl}/api/dashboard/whatsapp-agents`, {
          headers,
          signal,
        })
          .then(async (res) => {
            if (!res.ok) throw new Error("whatsapp-agents failed");
            return res.json();
          })
          .then((data) => {
            const count = isFiniteNumber(data?.activeCount)
              ? data.activeCount
              : null;
            updateActiveAgentsCount("whatsapp", count);
            setLoadingStates((prev) => ({ ...prev, whatsappAgents: false }));
          })
          .catch((error) => {
            if ((error as Error).name !== "AbortError") {
              console.error("Error fetching WhatsApp agents:", error);
            }
            setLoadingStates((prev) => ({ ...prev, whatsappAgents: false }));
          }),
      ]);

      // activeAgents derived
      const counts = Object.values(activeAgentsCounts.current);
      const knownCounts = counts.filter((v) => v != null) as number[];
      freshStats.activeAgents = knownCounts.length
        ? knownCounts.reduce((sum, v) => sum + v, 0)
        : null;

      setDashboardStats((prev) => ({ ...prev, ...freshStats }));
      setRecentCalls(freshRecentCalls);
      setAgents(freshAgents);

      saveCacheData(
        companyName,
        { ...dashboardStats, ...freshStats },
        freshRecentCalls,
        freshAgents,
      );
    } catch (error: any) {
      if (error.name !== "AbortError") {
        console.error("Error fetching dashboard data:", error);
        toast({
          title: "Error loading dashboard",
          description: "Failed to fetch dashboard data",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      isFetchingRef.current = false;
      abortControllerRef.current = null;

      // ensure flags are false (so missing values show as single spinner only)
      setLoadingStates((prev) => ({
        callStats: false,
        messageStats: false,
        voiceAgents: prev.voiceAgents ? false : prev.voiceAgents,
        smsAgents: prev.smsAgents ? false : prev.smsAgents,
        emailAgents: prev.emailAgents ? false : prev.emailAgents,
        whatsappAgents: prev.whatsappAgents ? false : prev.whatsappAgents,
        agentPerformance: prev.agentPerformance ? false : prev.agentPerformance,
      }));
    }
  };

  const updateActiveAgentsCount = (type: string, count: MaybeNumber) => {
    activeAgentsCounts.current[
      type as keyof typeof activeAgentsCounts.current
    ] = count;

    const counts = Object.values(activeAgentsCounts.current);
    const knownCounts = counts.filter((v) => v != null) as number[];
    const total = knownCounts.length
      ? knownCounts.reduce((sum, val) => sum + val, 0)
      : null;

    setDashboardStats((prev) => ({ ...prev, activeAgents: total }));
  };

  const updateAgentPerformance = async (
    activeAgents: any[],
    agentType: string,
    userId: string,
    companyName: string,
    signal?: AbortSignal,
  ): Promise<AgentPerformance[]> => {
    setLoadingStates((prev) => ({ ...prev, agentPerformance: true }));

    const agentPerformancePromises = activeAgents
      .slice(0, 5)
      .map(async (agent) => {
        try {
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
            "user-id": userId,
          };

          if (isAdmin && currentClientId) {
            const selectedClient = clients.find(
              (c) => c.id === currentClientId,
            );
            if (selectedClient) {
              headers["x-is-admin"] = "true";
              headers["x-company-name"] = selectedClient.company_name;
            }
          }

          const callsResponse = await fetch(`${serverUrl}/api/calls/list`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              filter_criteria: {
                agent_id: [agent.agent_id],
              },
            }),
            signal,
          });

          const agentCalls = callsResponse.ok ? await callsResponse.json() : [];
          const successfulCalls = agentCalls.filter(
            (call: any) => call.call_analysis?.call_successful === true,
          ).length;

          const successRate =
            agentCalls.length > 0
              ? (successfulCalls / agentCalls.length) * 100
              : 0;

          return {
            id: agent.agent_id,
            name: agent.agent_name || "Voice Agent",
            type: agentType,
            status: "Active",
            interactions: Array.isArray(agentCalls) ? agentCalls.length : 0,
            success: Math.round(successRate),
          };
        } catch (error) {
          if ((error as Error).name !== "AbortError") {
            console.error("Error fetching agent performance:", error);
          }
          return null;
        }
      });

    const agentPerformance = (
      await Promise.all(agentPerformancePromises)
    ).filter((agent): agent is AgentPerformance => agent !== null);

    setAgents(agentPerformance);
    setLoadingStates((prev) => ({
      ...prev,
      agentPerformance: false,
      voiceAgents: false,
    }));

    return agentPerformance;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Qualified":
        return "green";
      case "Appointment Set":
        return "blue";
      case "No Answer":
        return "yellow";
      case "Not Interested":
        return "red";
      default:
        return "gray";
    }
  };

  const getAgentStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "green";
      case "Paused":
        return "yellow";
      case "Inactive":
        return "red";
      default:
        return "gray";
    }
  };

  const getCallStatus = (call: RecentCall) => {
    if (call.call_analysis?.call_successful) {
      return call.call_analysis.user_sentiment || "Successful";
    }
    return "No Answer";
  };

  const formatDuration = (durationMs: number) => {
    const minutes = Math.floor((durationMs || 0) / 60000);
    const seconds = Math.floor(((durationMs || 0) % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleInviteUser = async () => {
    setIsInviting(true);
    try {
      await inviteUser(inviteEmail, inviteCompany);
      toast({
        title: "User Invited",
        description: "The user has been successfully invited.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error inviting user:", error);
      toast({
        title: "Error inviting user",
        description: "Failed to send invitation.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsInviting(false);
      onClose();
    }
  };

  const isAnyLoading = Object.values(loadingStates).some((loading) => loading);

  return (
    <Box minH="100vh" bg="gray.50">
      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="stretch">
          <Flex justify="space-between" align="center">
            <Heading size="lg" color="gray.800">
              Dashboard Overview
            </Heading>
            <Text color="gray.600">
              Welcome back! Here's what's happening with your campaigns.
            </Text>
          </Flex>

          <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={6}>
            <Card
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px) scale(1.02)",
                shadow: "xl",
              }}
            >
              <CardBody>
                <TopMetricStat
                  label="Total Calls"
                  subLabel="This Month"
                  value={dashboardStats.totalCalls}
                  valueLoading={loadingStates.callStats}
                  valueColor="blue.500"
                  valueFormatter={(n) => n.toLocaleString()}
                  changeValue={dashboardStats.totalCallsChange}
                />
              </CardBody>
            </Card>

            <Card
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px) scale(1.02)",
                shadow: "xl",
              }}
            >
              <CardBody>
                <TopMetricStat
                  label="Contact Rate"
                  subLabel="This Month"
                  value={dashboardStats.contactRate}
                  valueLoading={loadingStates.callStats}
                  valueColor="green.500"
                  valueFormatter={(n) => `${n.toFixed(1)}%`}
                  changeValue={dashboardStats.contactRateChange}
                />
              </CardBody>
            </Card>

            <Card
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px) scale(1.02)",
                shadow: "xl",
              }}
            >
              <CardBody>
                <TopMetricStat
                  label="Total SMS"
                  subLabel="This Month"
                  value={dashboardStats.totalSMS}
                  valueLoading={loadingStates.messageStats}
                  valueColor="purple.500"
                  valueFormatter={(n) => n.toLocaleString()}
                  changeValue={dashboardStats.smsChangePercent}
                />
              </CardBody>
            </Card>

            <Card
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px) scale(1.02)",
                shadow: "xl",
              }}
            >
              <CardBody>
                <TopMetricStat
                  label="Total WhatsApp"
                  subLabel="This Month"
                  value={dashboardStats.totalWhatsApp}
                  valueLoading={loadingStates.messageStats}
                  valueColor="green.600"
                  valueFormatter={(n) => n.toLocaleString()}
                  changeValue={dashboardStats.whatsappChangePercent}
                />
              </CardBody>
            </Card>

            {process.env.REACT_APP_IS_MAIN_APP === "true" && (
              <Card
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-4px) scale(1.02)",
                  shadow: "xl",
                }}
              >
                <CardBody>
                  <TopMetricStat
                    label="Total Emails"
                    subLabel="This Month"
                    value={dashboardStats.totalEmails}
                    valueLoading={loadingStates.messageStats}
                    valueColor="orange.500"
                    valueFormatter={(n) => n.toLocaleString()}
                    changeValue={dashboardStats.emailChangePercent}
                  />
                </CardBody>
              </Card>
            )}
          </Grid>

          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
            <Card
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-2px)",
                shadow: "lg",
              }}
            >
              <CardHeader>
                <Heading size="md" color="gray.800">
                  Active Agents
                </Heading>
              </CardHeader>
              <CardBody>
                {loadingStates.voiceAgents || loadingStates.agentPerformance ? (
                  <Center py={8}>
                    <Spinner size="lg" color="primary.500" />
                  </Center>
                ) : agents.length === 0 ? (
                  <Text color="gray.600" textAlign="center" py={4}>
                    No active agents found
                  </Text>
                ) : (
                  <VStack spacing={4} align="stretch">
                    {agents.map((agent) => (
                      <Box key={agent.id} p={4} bg="gray.50" borderRadius="md">
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text fontWeight="medium" color="gray.800">
                            {agent.name}
                          </Text>
                          <Badge
                            colorScheme={getAgentStatusColor(agent.status)}
                          >
                            {agent.status}
                          </Badge>
                        </Flex>
                        <HStack
                          justify="space-between"
                          fontSize="sm"
                          color="gray.600"
                        >
                          <Text>Type: {agent.type}</Text>
                          <Text>
                            {agent.type === "Voice"
                              ? "Calls"
                              : agent.type === "Email"
                                ? "Emails"
                                : "Messages"}
                            : {agent.interactions}
                          </Text>
                          <Text>Success: {agent.success}%</Text>
                        </HStack>
                        <Progress
                          value={agent.success}
                          colorScheme="green"
                          size="sm"
                          mt={2}
                          bg="gray.200"
                        />
                      </Box>
                    ))}
                  </VStack>
                )}
              </CardBody>
            </Card>

            <Card
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-2px)",
                shadow: "lg",
              }}
            >
              <CardHeader>
                <Heading size="md" color="gray.800">
                  Recent Calls
                </Heading>
              </CardHeader>
              <CardBody>
                {loadingStates.callStats ? (
                  <Center py={8}>
                    <Spinner size="lg" color="primary.500" />
                  </Center>
                ) : recentCalls.length === 0 ? (
                  <Text color="gray.600" textAlign="center" py={4}>
                    No recent calls found
                  </Text>
                ) : (
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Agent</Th>
                        <Th>Status</Th>
                        <Th>Duration</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {recentCalls.map((call) => (
                        <Tr key={call.call_id}>
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="medium" fontSize="sm">
                                {call.agent_name || "Unknown"}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {call.to_number || "N/A"}
                              </Text>
                            </VStack>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={getStatusColor(getCallStatus(call))}
                              size="sm"
                            >
                              {getCallStatus(call)}
                            </Badge>
                          </Td>
                          <Td>
                            <Text fontSize="sm">
                              {formatDuration(call.duration_ms || 0)}
                            </Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Grid>

          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-2px)",
              shadow: "lg",
            }}
          >
            <CardHeader>
              <Heading size="md" color="gray.800">
                Usage Overview
              </Heading>
            </CardHeader>
            <CardBody>
              <Grid
                templateColumns="repeat(auto-fit, minmax(150px, 1fr))"
                gap={6}
              >
                <Box textAlign="center">
                  <BigValueOrSpinner
                    value={dashboardStats.totalLeads}
                    loading={isAnyLoading}
                    color="blue.500"
                    formatter={(n) => n.toLocaleString()}
                  />
                  <Text fontSize="sm" color="gray.600">
                    Total Leads
                  </Text>
                </Box>

                <Box textAlign="center">
                  <BigValueOrSpinner
                    value={dashboardStats.activeAgents}
                    loading={isAnyLoading}
                    color="green.500"
                    formatter={(n) => n.toLocaleString()}
                  />
                  <Text fontSize="sm" color="gray.600">
                    Active Agents
                  </Text>
                </Box>

                <Box textAlign="center">
                  <BigValueOrSpinner
                    value={dashboardStats.minutesUsed}
                    loading={isAnyLoading}
                    color="primary.500"
                    formatter={(n) => n.toLocaleString()}
                  />
                  <Text fontSize="sm" color="gray.600">
                    Minutes Used
                  </Text>
                </Box>

                <Box textAlign="center">
                  <BigValueOrSpinner
                    value={dashboardStats.callsAnswered}
                    loading={isAnyLoading}
                    color="purple.500"
                    formatter={(n) => n.toLocaleString()}
                  />
                  <Text fontSize="sm" color="gray.600">
                    Calls Answered
                  </Text>
                </Box>
              </Grid>
            </CardBody>
          </Card>
        </VStack>
      </Container>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="secondary.800">Invite New User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel color="secondary.700">Email Address</FormLabel>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@company.com"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel color="secondary.700">Company Name</FormLabel>
                <Input
                  value={inviteCompany}
                  onChange={(e) => setInviteCompany(e.target.value)}
                  placeholder="Company Name"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="primary.500"
              color="white"
              _hover={{ bg: "primary.600" }}
              onClick={handleInviteUser}
              isLoading={isInviting}
              loadingText="Sending..."
            >
              Send Invitation
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Dashboard;
